import { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../../components/ui/alert-dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Ban, Download, Loader2, Mail, RotateCw } from 'lucide-react';
import { getPageNumbers } from '../../hooks/usePagination';
import type { EmailQueueStats } from '../../lib/api/emailQueue';
import { EmailQueueDetailDialog } from './EmailQueueDetailDialog';
import { QueueRow, humanise } from './EmailQueueRow';
import { EMAIL_QUEUE_PAGE_SIZE, useEmailQueue } from './useEmailQueue';

/** Status tabs: their counts double as the at-a-glance summary. */
const STATUS_TABS: Array<{ value: string; label: string; stat: keyof EmailQueueStats }> = [
  { value: 'all', label: 'All', stat: 'total' },
  { value: 'pending', label: 'Pending', stat: 'pending' },
  { value: 'sending', label: 'Sending', stat: 'sending' },
  { value: 'sent', label: 'Sent', stat: 'sent_total' },
  { value: 'failed', label: 'Failed', stat: 'failed' },
  { value: 'cancelled', label: 'Cancelled', stat: 'cancelled_total' },
  { value: 'skipped', label: 'Skipped', stat: 'skipped_total' },
];

const SIDES = [
  { value: 'customer', label: 'Customers' },
  { value: 'supplier', label: 'Suppliers' },
];

const PERIODS = [
  { value: 'all', label: 'All Time' },
  { value: '24h', label: 'Last 24 hrs' },
  { value: '48h', label: 'Last 48 hrs' },
  { value: '7d', label: 'Last 7 days' },
];

interface QueuePagerProps {
  /** Which pager this is, for screen readers: the table has one above and one below. */
  label: string;
  page: number;
  totalPages: number;
  totalCount: number;
  onPage: (page: number) => void;
}

/**
 * The page numbers to offer: the pages around this one, plus the first and last
 * so a long queue can always be crossed, with `null` marking a gap between them.
 */
function pagerSlots(page: number, totalPages: number): Array<number | null> {
  const around = getPageNumbers(page, totalPages);
  const first = around[0] ?? 1;
  const last = around[around.length - 1] ?? totalPages;
  return [
    ...(first > 1 ? [1] : []),
    ...(first > 2 ? [null] : []),
    ...around,
    ...(last < totalPages - 1 ? [null] : []),
    ...(last < totalPages ? [totalPages] : []),
  ];
}

function QueuePager({ label, page, totalPages, totalCount, onPage }: QueuePagerProps) {
  return (
    <nav aria-label={label} className="flex flex-wrap items-center justify-between gap-2">
      <p className="text-sm text-muted-foreground">
        Page {page} of {totalPages} ({totalCount.toLocaleString()} total)
      </p>
      <div className="flex items-center gap-1">
        {pagerSlots(page, totalPages).map((slot, index) => (slot === null ? (
          <span key={`gap-${index}`} className="px-1 text-muted-foreground" aria-hidden="true">…</span>
        ) : (
          <Button
            key={slot}
            variant={slot === page ? 'default' : 'outline'}
            size="sm"
            className="h-8 min-w-8 px-2"
            aria-label={`Page ${slot}`}
            aria-current={slot === page ? 'page' : undefined}
            onClick={() => onPage(slot)}
          >
            {slot}
          </Button>
        )))}
      </div>
    </nav>
  );
}

export default function EmailQueueDashboard() {
  const { user } = useAuth();
  // Same gate as Platform Admin (Helix tenancy) — the queue is platform
  // infrastructure and every endpoint behind it 403s other tenants. Without
  // this a customer admin who typed the URL saw a page of raw
  // "Request failed with status code 403" toasts.
  const isHelixUser = user?.platform === 'HELIX' || user?.company_type === 'helix';
  const queue = useEmailQueue(isHelixUser);
  const [viewingId, setViewingId] = useState<number | null>(null);
  const { stats, options, typeFilter, customerFilter } = queue;

  if (!isHelixUser) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">You do not have access to the Email Queue.</p>
      </div>
    );
  }

  if (queue.loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const sideCount = (side: string): number =>
    options.sides.find((option) => option.value === side)?.count ?? 0;
  const pending = stats?.pending ?? 0;
  const failed = stats?.failed ?? 0;
  const paged = queue.totalCount > EMAIL_QUEUE_PAGE_SIZE;
  const pagerProps = {
    page: queue.page,
    totalPages: queue.totalPages,
    totalCount: queue.totalCount,
    onPage: queue.setPage,
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <Mail className="h-6 w-6" />
            Email Queue
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Monitor and manage outbound email delivery. Click an email to see it. Emails
            are kept for 7 days once they are finished with.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={queue.refresh} disabled={queue.refreshing}>
          <RotateCw className={`h-4 w-4 mr-2 ${queue.refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Status */}
      <Tabs value={queue.statusFilter} onValueChange={queue.setStatusFilter}>
        <TabsList className="flex h-auto flex-wrap justify-start">
          {STATUS_TABS.map((tab) => (
            <TabsTrigger key={tab.value} value={tab.value} className="gap-1.5">
              {tab.label}
              {stats && (
                <span className="rounded-full bg-muted px-1.5 text-xs tabular-nums">
                  {stats[tab.stat].toLocaleString()}
                </span>
              )}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {/* Period: on the screen, not in a list, so the choices can be seen at a glance. */}
      <div role="group" aria-label="Period" className="flex flex-wrap items-center gap-2">
        {PERIODS.map(({ value, label }) => (
          <Button
            key={value}
            variant={queue.periodFilter === value ? 'default' : 'outline'}
            size="sm"
            aria-pressed={queue.periodFilter === value}
            onClick={() => queue.setPeriodFilter(value)}
          >
            {label}
          </Button>
        ))}
      </div>

      {/* Filters and Bulk Actions */}
      <div className="flex flex-col sm:flex-row flex-wrap items-start sm:items-center gap-3">
        <Input
          placeholder="Search recipient or subject..."
          aria-label="Search recipient or subject"
          value={queue.searchInput}
          onChange={(e) => queue.handleSearchChange(e.target.value)}
          className="w-full sm:w-64"
        />
        {(options.customers.length > 1 || customerFilter !== 'all') && (
          <Select value={customerFilter} onValueChange={queue.setCustomerFilter}>
            <SelectTrigger className="w-full sm:w-52" aria-label="Customer">
              <SelectValue placeholder="Customer" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Customers</SelectItem>
              {options.customers.map((customer) => (
                <SelectItem key={customer.value} value={customer.value}>
                  {customer.label} ({customer.count.toLocaleString()})
                </SelectItem>
              ))}
              {/* The chosen customer can drop out of the options when a search narrows them. */}
              {customerFilter !== 'all' && !options.customers.some((customer) => customer.value === customerFilter) && (
                <SelectItem value={customerFilter}>Selected customer (0)</SelectItem>
              )}
            </SelectContent>
          </Select>
        )}
        <Select value={queue.sideFilter} onValueChange={queue.setSideFilter}>
          <SelectTrigger className="w-full sm:w-44" aria-label="Recipient side">
            <SelectValue placeholder="Recipient side" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Recipients</SelectItem>
            {SIDES.map(({ value, label }) => (
              <SelectItem key={value} value={value}>
                {options.sides.length > 0 ? `${label} (${sideCount(value).toLocaleString()})` : label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={typeFilter} onValueChange={queue.setTypeFilter}>
          <SelectTrigger className="w-full sm:w-56" aria-label="Type">
            <SelectValue placeholder="Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            {[...options.types]
              .sort((a, b) => humanise(a.value).localeCompare(humanise(b.value)))
              .map((type) => (
              <SelectItem key={type.value} value={type.value}>
                {humanise(type.value)} ({type.count.toLocaleString()})
              </SelectItem>
            ))}
            {/* The chosen type can drop out of the options when a search or tab narrows them. */}
            {typeFilter !== 'all' && !options.types.some((type) => type.value === typeFilter) && (
              <SelectItem value={typeFilter}>{humanise(typeFilter)} (0)</SelectItem>
            )}
          </SelectContent>
        </Select>

        <div className="flex-1" />

        <Button
          variant="outline"
          size="sm"
          disabled={queue.exporting || queue.totalCount === 0}
          onClick={queue.handleExport}
        >
          {queue.exporting
            ? <Loader2 className="h-4 w-4 mr-1 animate-spin" />
            : <Download className="h-4 w-4 mr-1" />}
          Export
        </Button>

        {/* Bulk Actions */}
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="outline" size="sm" className="text-red-600 border-red-200 hover:bg-red-50" disabled={queue.bulkActionLoading || pending === 0}>
              <Ban className="h-4 w-4 mr-1" />
              {queue.scoped ? `Cancel ${pending.toLocaleString()} Pending` : 'Cancel All Pending'}
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Cancel {queue.scoped ? 'these' : 'all'} pending emails?</AlertDialogTitle>
              <AlertDialogDescription>
                This will cancel {pending.toLocaleString()} pending email(s). This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Go Back</AlertDialogCancel>
              <AlertDialogAction onClick={queue.handleCancelAll} className="bg-red-600 hover:bg-red-700">
                Cancel {queue.scoped ? 'These' : 'All'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <Button
          variant="outline"
          size="sm"
          className="text-amber-600 border-amber-200 hover:bg-amber-50"
          disabled={queue.bulkActionLoading || failed === 0}
          onClick={queue.handleRetryAll}
        >
          <RotateCw className="h-4 w-4 mr-1" />
          {queue.scoped ? `Retry ${failed.toLocaleString()} Failed` : 'Retry All Failed'}
        </Button>
      </div>

      {queue.loadFailed && queue.entries.length > 0 && (
        <p className="text-sm text-red-600" role="alert">
          The queue could not be refreshed. These are the last rows loaded.
        </p>
      )}

      {paged && <QueuePager label="Email queue pages, above the table" {...pagerProps} />}

      {/* Queue Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Recipient</TableHead>
                <TableHead>Subject</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>Sent / Failed</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {queue.entries.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-12 text-muted-foreground">
                    <Mail className="h-8 w-8 mx-auto mb-2 opacity-40" />
                    {queue.loadFailed ? (
                      <>
                        <p>The queue could not be loaded.</p>
                        <Button variant="outline" size="sm" className="mt-3" onClick={queue.refresh}>
                          Try again
                        </Button>
                      </>
                    ) : queue.filtered ? 'No emails match these filters' : 'No emails found'}
                  </TableCell>
                </TableRow>
              ) : (
                queue.entries.map((entry) => (
                  <QueueRow
                    key={entry.id}
                    entry={entry}
                    showCustomer={options.customers.length > 1}
                    cancellingId={queue.cancellingId}
                    retryingId={queue.retryingId}
                    onCancel={queue.handleCancel}
                    onRetry={queue.handleRetry}
                    onView={setViewingId}
                  />
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {paged && <QueuePager label="Email queue pages, below the table" {...pagerProps} />}

      <EmailQueueDetailDialog entryId={viewingId} onClose={() => setViewingId(null)} />
    </div>
  );
}
